// src/pages/admin/RolesPage.jsx
import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  UserPlus, UserX, Crown, Search,
  Loader, UserCheck, Clock, Trash2, Edit, Users, Ellipsis, Briefcase, CircleUser,
} from 'lucide-react'
import Select from 'react-select'
import { motion, AnimatePresence } from 'framer-motion'
import ConfirmModal from '@/widgets/ConfirmModal'
import DataTable from '@/widgets/DataTable'
import {
  getAdminUsers, createAdminInvite,
  updateAdminStatus, updateAdminRole,
  getCountries, syncLearnerFromUser, updateAdmin
} from '@/api/kuizy'
import { Mail, User, Phone, Globe } from 'lucide-react'
import { formatDateTime } from '../../settings/master'
import Drawer from '@/layout/Drawer'

export default function RolesPage() {
  const myRoles = JSON.parse(localStorage.getItem('roles') || '[]')
  const isMaster = myRoles.includes('MASTER_ADMIN')
  if (!myRoles.some(r => r === 'MASTER_ADMIN' || r === 'ADMIN'))
    return <div className="p-6 text-center text-red-600 font-semibold">คุณไม่มีสิทธิ์เข้าหน้านี้</div>

  const phoneRx = /^[0-9]{6,15}$/
  const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [searchTxt, setSearch] = useState('')
  const [statusFilter, setStatus] = useState('all')
  const [roleFilter, setRole] = useState('all')

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(() => {
    const saved = localStorage.getItem('admin_users_per_page')
    return saved ? parseInt(saved) : 10
  })
  const [sortBy, setSortBy] = useState('created_at')
  const [sortDir, setSortDir] = useState('desc')
  const [formEdit, setFormEdit] = useState({
    id: '',
    email: '',
    first_name: '',
    last_name: '',
    phone_code: '+66',
    phone: '',
    display_name: '',
    job_title: ''
  })
  const [saving, setSaving] = useState(false)
  const [drawer, setDrawer] = useState(false)

  const [invite, setInvite] = useState({
    open: false,
    email: '',
    role: 'CREATOR',
    first_name: '',
    last_name: '',
    phone_code: '+66',
    phone: '',
    country: 'TH'
  })
  const [inviteBusy, setIBusy] = useState(false)
  const [busy, setBusy] = useState({ del: '', role: '', learner: '' })
  const [confirm, setConfirm] = useState({ open: false, mode: '', payload: {} })
  const [countries, setCountries] = useState([])
  const [editing, setEditing] = useState(false)
  const phoneSanitized = formEdit.phone?.replace(/^0+/, '')
  const isPhoneOk = !phoneSanitized || phoneRx.test(phoneSanitized)
  const isEmailOk = emailRx.test(formEdit.email)
  useEffect(() => {
    localStorage.setItem('admin_users_per_page', String(perPage))
  }, [perPage])

  useEffect(() => {
    getCountries().then(r => setCountries(r?.data || [])).catch(() => setCountries([]))
  }, [])

  const saveEdit = async (e) => {
    e.preventDefault()
    if (busy.role) return
    setBusy(b => ({ ...b, role: formEdit.id }))
    try {
      await updateAdmin(formEdit.id, formEdit)
      setDrawer(false)
      fetchList()

      setConfirm({ open: true, mode: 'success', payload: { title: 'บันทึกสำเร็จ', msg: 'อัปเดตข้อมูลเรียบร้อย' } })
    } catch (e) {
      setConfirm({ open: true, mode: 'error', payload: { title: 'อัปเดตไม่สำเร็จ', msg: e.response?.data?.detail || 'เกิดข้อผิดพลาด' } })
    } finally { setBusy(b => ({ ...b, role: '' })) }
  }

  const fetchList = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const params = {
        search: searchTxt.trim() || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        role: roleFilter === 'all' ? undefined : roleFilter,
        page, per_page: perPage, sort_by: sortBy, sort_dir: sortDir
      }
      const { data } = await getAdminUsers(params)
      setRows(Array.isArray(data?.rows) ? data.rows : [])
      setTotal(Number.isFinite(data?.total) ? data.total : (Array.isArray(data) ? data.length : 0))
    } catch {
      setErr('โหลดรายชื่อไม่สำเร็จ'); setRows([]); setTotal(0)
    } finally { setLoading(false) }
  }, [searchTxt, statusFilter, roleFilter, page, perPage, sortBy, sortDir])

  useEffect(() => { fetchList() }, [fetchList])

  const doInvite = async () => {
    if (inviteBusy) return
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const countryRx = /^[A-Za-z]{2}$/
    if (!emailRx.test(invite.email.trim())) {
      setConfirm({ open: true, mode: 'error', payload: { title: 'ข้อมูลไม่ครบ', msg: 'อีเมลไม่ถูกต้อง' } }); return
    }
    if (!invite.first_name.trim() || !invite.last_name.trim()) {
      setConfirm({ open: true, mode: 'error', payload: { title: 'ข้อมูลไม่ครบ', msg: 'กรุณากรอกชื่อและนามสกุล' } }); return
    }
    if (!invite.phone_code.trim() || !invite.phone.trim()) {
      setConfirm({ open: true, mode: 'error', payload: { title: 'ข้อมูลไม่ครบ', msg: 'กรุณากรอกรหัสประเทศและเบอร์โทรศัพท์' } }); return
    }
    if (!countryRx.test((invite.country || '').trim())) {
      setConfirm({ open: true, mode: 'error', payload: { title: 'ข้อมูลไม่ครบ', msg: 'ประเทศต้องเป็นรหัส 2 ตัว เช่น TH, US' } }); return
    }
    setIBusy(true)
    try {
      await createAdminInvite({
        email: invite.email.trim(),
        role: invite.role,
        first_name: invite.first_name.trim(),
        last_name: invite.last_name.trim(),
        phone_code: invite.phone_code.trim().startsWith('+') ? invite.phone_code.trim() : `+${invite.phone_code.trim()}`,
        phone: invite.phone.trim(),
        country: invite.country.trim().toUpperCase()
      })
      setInvite({ open: false, email: '', role: 'CREATOR', first_name: '', last_name: '', phone_code: '+66', phone: '', country: 'TH' })
      setConfirm({ open: true, mode: 'success', payload: { title: 'ส่งอีเมลสำเร็จ', msg: 'ส่งคำเชิญพร้อมข้อมูลผู้ใช้เรียบร้อย' } })
      fetchList()
    } catch {
      setConfirm({ open: true, mode: 'error', payload: { title: 'เชิญไม่สำเร็จ', msg: 'ไม่สามารถส่งคำเชิญได้' } })
    } finally { setIBusy(false) }
  }

  const doToggleLearner = async (r) => {
    if (busy.learner) return
    setBusy(b => ({ ...b, learner: r.id }))
    try {
      await syncLearnerFromUser(r.id, !r.is_learner)
      fetchList()
      setConfirm({ open: true, mode: 'success', payload: { title: 'บันทึกสำเร็จ', msg: 'อัปเดตสถานะ Learner เรียบร้อย' } })
    } catch (e) {
      setConfirm({ open: true, mode: 'error', payload: { title: 'อัปเดตไม่สำเร็จ', msg: e.response?.data?.detail || 'เกิดข้อผิดพลาด' } })
    } finally {
      setBusy(b => ({ ...b, learner: '' }))
    }
  }

  const askDelete = (uid, email) =>
    setConfirm({ open: true, mode: 'delete', payload: { uid, email, title: 'ยืนยันลบผู้ใช้', msg: `ต้องการลบผู้ใช้ ${email} หรือไม่ ?` } })

  const doDelete = async () => {
    const { uid } = confirm.payload
    setBusy(b => ({ ...b, del: uid }))
    try {
      await updateAdminStatus(uid, '3')
      fetchList()
      setConfirm({ open: true, mode: 'success', payload: { title: 'ลบสำเร็จ', msg: 'ดำเนินการเรียบร้อย' } })
    } catch (e) {
      setConfirm({ open: true, mode: 'error', payload: { title: 'ลบไม่สำเร็จ', msg: e.response?.data?.detail || 'เกิดข้อผิดพลาด' } })
    } finally { setBusy(b => ({ ...b, del: '' })) }
  }

  const doChangeRole = async (uid, role) => {
    if (busy.role) return
    setBusy(b => ({ ...b, role: uid }))
    try {
      await updateAdminRole(uid, role)
      fetchList()
      setConfirm({ open: true, mode: 'success', payload: { title: 'บันทึกสำเร็จ', msg: 'อัปเดต Role เรียบร้อย' } })
    } catch (e) {
      setConfirm({ open: true, mode: 'error', payload: { title: 'อัปเดตไม่สำเร็จ', msg: e.response?.data?.detail || 'เกิดข้อผิดพลาด' } })
    } finally { setBusy(b => ({ ...b, role: '' })) }
  }

  const closeConfirm = () => setConfirm({ open: false, mode: '', payload: {} })

  const columns = useMemo(() => [
    {
      key: "full_name",
      title: (
        <button onClick={() => { const nd = sortBy === 'full_name' && sortDir === 'asc' ? 'desc' : 'asc'; setSortBy('full_name'); setSortDir(nd); setPage(1) }} className={`inline-flex items-center gap-1 select-none cursor-pointer ${sortBy === 'full_name' ? 'font-semibold text-gray-900' : 'text-gray-700 hover:text-gray-900'}`}>
          <span>ชื่อ-สกุล</span>
        </button>
      ),
      sortable: true,
      render: (r) => (
        <div className="flex items-center gap-2">
          {r.avatar_url ? <img src={r.avatar_url} className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 bg-gray-200 rounded-full" />}
          <span>{r.full_name || `${r.first_name || ''} ${r.last_name || ''}`}</span>
        </div>
      )
    },
    {
      key: "email",
      title: (
        <button onClick={() => { const nd = sortBy === 'email' && sortDir === 'asc' ? 'desc' : 'asc'; setSortBy('email'); setSortDir(nd); setPage(1) }} className={`inline-flex items-center gap-1 select-none cursor-pointer ${sortBy === 'email' ? 'font-semibold text-gray-900' : 'text-gray-700 hover:text-gray-900'}`}>
          <span>อีเมล</span>
        </button>
      ),
      sortable: true
    },
    {
      key: "phone",
      title: "โทรศัพท์",
      render: (r) => `${r.phone_code !== null && r.phone_code !== undefined && r.phone_code !== '' ? (r.phone_code + ' ') : ' '}${r.phone !== null && r.phone !== undefined && r.phone !== '' ? r.phone : ' -'}`
    },
    {
      key: "role",
      title: (
        <button onClick={() => { const nd = sortBy === 'role' && sortDir === 'asc' ? 'desc' : 'asc'; setSortBy('role'); setSortDir(nd); setPage(1) }} className={`inline-flex items-center gap-1 select-none cursor-pointer ${sortBy === 'role' ? 'font-semibold text-gray-900' : 'text-gray-700 hover:text-gray-900'}`}>
          <span>Role</span>
        </button>
      ),
      sortable: true,
      render: (r) => {
        const role = r.roles.includes('ADMIN') ? 'ADMIN' : r.roles.includes('CREATOR') ? 'CREATOR' : 'MASTER_ADMIN'
        return (
          isMaster && role !== 'MASTER_ADMIN'
            ? <select value={role} disabled={busy.role === r.id} onChange={e => doChangeRole(r.id, e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1 text-sm"><option value="ADMIN">ADMIN</option><option value="CREATOR">CREATOR</option></select>
            : role
        )
      }
    },
    // {
    //   key: "is_learner",
    //   title: "Learner",
    //   align: "center",
    //   width: 90,
    //   render: (r) => (
    //     <input
    //       type="checkbox"
    //       checked={!!r.is_learner}
    //       onChange={() => doToggleLearner(r)}
    //       disabled={busy.learner === r.id}
    //       className="w-4 h-4 accent-primary cursor-pointer disabled:opacity-50"
    //     />
    //   )
    // },
    {
      key: "status",
      title: (
        <button onClick={() => { const nd = sortBy === 'status' && sortDir === 'asc' ? 'desc' : 'asc'; setSortBy('status'); setSortDir(nd); setPage(1) }} className={`inline-flex items-center gap-1 select-none cursor-pointer ${sortBy === 'status' ? 'font-semibold text-gray-900' : 'text-gray-700 hover:text-gray-900'}`}>
          <span>สถานะ</span>
        </button>
      ),
      align: "center",
      sortable: true,
      render: (r) => (
        r.status === 1 ? <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-2 py-[2px] rounded-full text-[10px]"><UserCheck size={10} />active</span>
          : r.status === 0 ? <span className="inline-flex items-center gap-1 bg-yellow-50 text-yellow-700 px-2 py-[2px] rounded-full text-[10px]"><Clock size={10} />pending</span>
            : <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 px-2 py-[2px] rounded-full text-[10px]"><Trash2 size={10} />trash</span>
      )
    },
    {
      key: "content_count",
      title: <div className="text-right">เนื้อหา</div>,
      render: (r) => (
        <span className="block text-right cursor-pointer hover:text-primary">
          {r.content_count || 0}
        </span>
      )
    },
    {
      key: "test_count",
      title: <div className="text-right">ชุดข้อสอบ</div>,
      render: (r) => (
        <span className="block text-right cursor-pointer hover:text-primary">
          {r.test_count || 0}
        </span>
      )
    },
    {
      key: "created_at",
      title: (
        <button onClick={() => { const nd = sortBy === 'created_at' && sortDir === 'asc' ? 'desc' : 'asc'; setSortBy('created_at'); setSortDir(nd); setPage(1) }} className={`inline-flex items-center gap-1 select-none cursor-pointer ${sortBy === 'created_at' ? 'font-semibold text-gray-900' : 'text-gray-700 hover:text-gray-900'}`}>
          <span>สร้างเมื่อ</span>
        </button>
      ),
      align: "right",
      sortable: true,
      render: (r) => formatDateTime(r.created_at)
    },
    {
      key: "__actions",
      title: "จัดการ",
      align: "center",
      pinEnd: true,
      width: 80,
      render: (r) => {
        const role = r.roles.includes('ADMIN') ? 'ADMIN' : r.roles.includes('CREATOR') ? 'CREATOR' : 'MASTER_ADMIN'
        let canDel = false
        if (isMaster) canDel = true
        else if (role === 'CREATOR') canDel = true
        return canDel && role !== "MASTER_ADMIN" ? (
          // <button disabled={busy.del === r.id} onClick={() => askDelete(r.id, r.username)} className="text-danger hover:text-danger/80 disabled:opacity-50">
          //   {busy.del === r.id ? <Loader size={16} className="animate-spin" /> : <UserX size={16} />}
          // </button>
          <button title="แก้ไข" onClick={() => { setEditing(true); setFormEdit({ id: r.id, first_name: r.first_name, last_name: r.last_name, display_name: r.display_name || '', job_title: r.job_title || '', phone_code: r.phone_code || '', phone: r.phone || '', email: r.email, username: r.username, }); setDrawer(true) }} className="text-primary hover:text-primary/80"><Edit size={16} /></button>
        ) : null
      }
    }
  ], [isMaster, busy, sortBy, sortDir])


  const statusOptions = [
    { value: 'all', label: 'ทั้งหมด' },
    { value: '1', label: 'Active' },
    { value: '0', label: 'Pending' },
    { value: '3', label: 'Trash' }
  ]
  const roleOptions = [
    { value: 'all', label: 'ทั้งหมด' },
    { value: 'MASTER_ADMIN', label: 'MASTER_ADMIN' },
    { value: 'ADMIN', label: 'ADMIN' },
    { value: 'CREATOR', label: 'CREATOR' }
  ]

  return (
    <motion.section className="flex flex-col gap-6 p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="flex flex-wrap gap-3 items-center justify-between">
        <h1 className="flex items-center gap-2 text-lg font-semibold cursor-pointer" onClick={fetchList}><Crown size={20} /> จัดการผู้ใช้งานระบบ ({total})</h1>
        <div className="flex flex-wrap gap-2">
          <button
            disabled={inviteBusy}
            onClick={() => setInvite(i => ({ ...i, open: true }))}
            className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50"
          >
            {inviteBusy ? <Loader size={16} className="animate-spin" /> : <UserPlus size={16} />} เชิญผู้ใช้งานระบบ
          </button>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.05 }} className="bg-white rounded-xl shadow-md p-4 flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1 w-48">
          <label className="flex items-center gap-1 text-sm"><UserCheck size={16} /> สถานะ</label>
          <Select classNamePrefix="rs" options={statusOptions} value={statusOptions.find(o => o.value === statusFilter)} onChange={o => { setStatus(o.value); setPage(1) }} />
        </div>
        <div className="flex flex-col gap-1 w-48">
          <label className="flex items-center gap-1 text-sm"><Crown size={16} /> Role</label>
          <Select classNamePrefix="rs" options={roleOptions} value={roleOptions.find(o => o.value === roleFilter)} onChange={o => { setRole(o.value); setPage(1) }} />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <label className="flex items-center gap-1 text-sm"><Search size={16} /> ค้นหา</label>
          <div className="relative">
            <input placeholder="ค้นหาอีเมล / ชื่อ" value={searchTxt} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && (setPage(1), fetchList())} className="border border-gray-300 rounded-lg px-10 py-2 w-full text-sm focus:ring-2 focus:ring-primary" />
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          </div>
        </div>
        <button onClick={() => { setPage(1); fetchList() }} className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm"><Search size={16} /> ค้นหา</button>
      </motion.div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        total={total}
        page={page}
        perPage={perPage}
        sortBy={sortBy}
        sortDir={sortDir}
        onPageChange={setPage}
        onPerPageChange={(n) => { setPerPage(n); setPage(1) }}
        onSortChange={(key, dir) => { setSortBy(key); setSortDir(dir); setPage(1) }}
        emptyText={err || 'ไม่พบข้อมูล'}
      />

      <InviteModal
        invite={invite}
        setInvite={setInvite}
        isMaster={isMaster}
        doInvite={doInvite}
        inviteBusy={inviteBusy}
        countries={countries}
      />

      <AnimatePresence>
        {confirm.open && (
          <ConfirmModal
            type={confirm.mode === 'delete' ? 'confirm' : 'alert'}
            title={confirm.payload.title}
            message={confirm.payload.msg}
            danger={confirm.mode === 'delete'}
            loading={confirm.mode === 'delete' && busy.del !== ''}
            onConfirm={confirm.mode === 'delete' ? doDelete : undefined}
            onCancel={confirm.mode === 'delete' ? (() => setConfirm({ open: false, mode: '', payload: {} })) : undefined}
            onClose={confirm.mode !== 'delete' ? (() => setConfirm({ open: false, mode: '', payload: {} })) : undefined}
          />
        )}
      </AnimatePresence>

      <Drawer open={drawer} title={'แก้ไขผู้ใช้งานในระบบ'} onClose={() => setDrawer(false)}>
        <form onSubmit={saveEdit} className="flex h-full flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1"><label className="flex items-center gap-1 text-sm"><User size={14} /> ชื่อ <span className="text-red-500 font-bold">*</span></label><input value={formEdit.first_name} onChange={e => setFormEdit({ ...formEdit, first_name: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:ring-2 focus:ring-primary" /></div>
            <div className="flex flex-col gap-1"><label className="flex items-center gap-1 text-sm"><CircleUser size={14} /> นามสกุล <span className="text-red-500 font-bold">*</span></label><input value={formEdit.last_name} onChange={e => setFormEdit({ ...formEdit, last_name: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:ring-2 focus:ring-primary" /></div>
            <div className="flex flex-col gap-1"><label className="flex items-center gap-1 text-sm"><User size={14} /> ชื่อเล่น</label><input value={formEdit.display_name} onChange={e => setFormEdit({ ...formEdit, display_name: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" /></div>
            <div className="flex flex-col gap-1"><label className="flex items-center gap-1 text-sm"><Briefcase size={14} /> ตำแหน่งงาน</label><input value={formEdit.job_title} onChange={e => setFormEdit({ ...formEdit, job_title: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" /></div>
            <div className="flex flex-col gap-1 sm:col-span-2"><label className="flex items-center gap-1 text-sm"><Mail size={14} /> อีเมล <span className="text-red-500 font-bold">*</span></label><input type="email" value={formEdit.email} onChange={e => setFormEdit({ ...formEdit, email: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:ring-2 focus:ring-primary" />{!isEmailOk && <small className="text-red-600 text-xs mt-1">รูปแบบอีเมลไม่ถูกต้อง</small>}</div>
            <div className="flex flex-col gap-1 sm:col-span-2"><label className="flex items-center gap-1 text-sm"><Phone size={14} /> โทรศัพท์ </label><div className="flex gap-2"><input value={formEdit.phone_code} onChange={e => setFormEdit({ ...formEdit, phone_code: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-24 focus:ring-2 focus:ring-primary text-center" /><input value={formEdit.phone} onChange={e => setFormEdit({ ...formEdit, phone: e.target.value.replace(/\D/g, '') })} placeholder='ใส่่เฉพาะตัวเลข 10 หลัก' className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1 focus:ring-2 focus:ring-primary" /></div>{!isPhoneOk && <small className="text-red-600 text-xs mt-1">เบอร์โทรไม่ถูกต้อง</small>}</div>
          </div>
          <button disabled={saving} className="bg-primary hover:bg-secondary disabled:opacity-50 text-white rounded-lg px-4 py-2">{saving ? <Loader size={16} className="animate-spin inline" /> : <span>{'บันทึกการแก้ไข'}</span>}</button>
        </form>
        <div className="sticky bottom-0  bg-white">
          <button type="button" disabled={saving} className="w-full flex items-center justify-center gap-2 border border-red-300 text-red-600 rounded-lg px-4 py-2 text-sm hover:bg-red-50 hover:border-red-500 disabled:opacity-50 "
            onClick={() => askDelete(formEdit.id, formEdit.email)}
          >
            ลบผู้ใช้งาน
          </button>
        </div>

      </Drawer>
    </motion.section>
  )
}

function InviteModal({ invite, setInvite, isMaster, doInvite, inviteBusy, countries }) {
  const selectStyle = { control: b => ({ ...b, minHeight: 42, borderRadius: 12 }) }
  const field = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary'
  const countryOptions = countries.map(c => ({ value: c.iso2, label: `${c.name_en} (${c.phone_code})` }))
  const countryValue = invite.country ? { value: invite.country, label: countries.find(c => c.iso2 === invite.country)?.name_en || invite.country } : null

  return (
    <AnimatePresence>
      {invite.open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} onClick={() => setInvite({ open: false, email: '', role: 'CREATOR', first_name: '', last_name: '', phone_code: '+66', phone: '', country: 'TH' })} className="fixed inset-0 bg-black/40 z-40" />
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} transition={{ duration: 0.25 }} className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(520px,92%)] bg-white rounded-xl shadow-lg z-50 p-6 flex flex-col gap-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-primary flex items-center gap-2"><UserPlus size={18} /> เชิญผู้ใช้งานระบบ</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium flex items-center gap-2"><Mail size={16} />อีเมล *</label>
                <input className={field} value={invite.email} onChange={e => setInvite(v => ({ ...v, email: e.target.value }))} placeholder="name@example.com" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">กำหนด Role *</label>
                <select className={field} value={invite.role} onChange={e => setInvite(v => ({ ...v, role: e.target.value }))}>
                  {isMaster && <option value="ADMIN">ADMIN</option>}
                  <option value="CREATOR">CREATOR</option>
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium flex items-center gap-2"><User size={16} />ชื่อจริง</label>
                <input className={field} value={invite.first_name} onChange={e => setInvite(v => ({ ...v, first_name: e.target.value }))} placeholder="ชื่อจริง" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium flex items-center gap-2"><User size={16} />นามสกุล</label>
                <input className={field} value={invite.last_name} onChange={e => setInvite(v => ({ ...v, last_name: e.target.value }))} placeholder="นามสกุล" />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium flex items-center gap-2"><Globe size={16} />ประเทศ</label>
                <Select
                  classNamePrefix="rs"
                  styles={selectStyle}
                  options={countryOptions}
                  value={countryValue}
                  onChange={o => {
                    const c = countries.find(x => x.iso2 === (o?.value || ''))
                    setInvite(v => ({ ...v, country: o?.value || 'TH', phone_code: c?.phone_code || v.phone_code }))
                  }}
                  placeholder="เลือกประเทศ"
                  isClearable
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium flex items-center gap-2"><Phone size={16} />รหัสประเทศ</label>
                <input className={field} value={invite.phone_code} onChange={e => setInvite(v => ({ ...v, phone_code: e.target.value }))} placeholder="+66" />
              </div>
              <div className="flex flex-col gap-1 md:col-span-2">
                <label className="text-sm font-medium flex items-center gap-2"><Phone size={16} />เบอร์โทรศัพท์</label>
                <input className={field} inputMode="numeric" pattern="[0-9]*" value={invite.phone} onChange={e => {
                  const onlyNumbers = e.target.value.replace(/\D/g, "")
                  setInvite(v => ({ ...v, phone: onlyNumbers }))
                }}
                  placeholder="ใส่เฉพาะตัวเลข 10 หลัก"
                />

              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button disabled={inviteBusy} onClick={() => setInvite({ open: false, email: '', role: 'CREATOR', first_name: '', last_name: '', phone_code: '+66', phone: '', country: 'TH' })} className="px-4 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-100 disabled:opacity-50">ยกเลิก</button>
              <button disabled={inviteBusy} onClick={doInvite} className="px-4 py-2 rounded-lg text-sm bg-primary hover:bg-secondary text-white font-semibold disabled:opacity-50 flex items-center gap-1">
                {inviteBusy && <Loader size={16} className="animate-spin" />}ส่งเชิญ
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
